import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTranslation } from "react-i18next";
import {
  bob,
  cloudLift,
  fanIn,
  floatOrbit,
  motionSafe,
  rise,
  shimmerSweep,
  softPulse,
  staggerIn,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "./studioDialogMotion";

const ACCENT_FICHA = "#0f766e";
const ACCENT_MOOD = "#7c3aed";

function HeroStage({ accent, variant = "ficha" }) {
  const isMood = variant === "moodboard";
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 128,
        mt: 1.5,
        mb: 0.25,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.07),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.35)}`,
        animation: `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        ...motionSafe,
      }}
    >
      {/* ambient orbs */}
      <Box
        sx={{
          position: "absolute",
          width: 90,
          height: 90,
          borderRadius: "50%",
          left: -18,
          top: -28,
          bgcolor: alpha(accent, 0.16),
          filter: "blur(2px)",
          animation: `${softPulse} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 70,
          height: 70,
          borderRadius: "50%",
          right: -10,
          bottom: -22,
          bgcolor: alpha(accent, 0.12),
          animation: `${floatOrbit} 5s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      {/* shimmer */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "42%",
            background: `linear-gradient(90deg, transparent, ${alpha(
              "#fff",
              0.45,
            )}, transparent)`,
            animation: `${shimmerSweep} 3.2s ease-in-out infinite`,
            animationDelay: "0.6s",
            ...motionSafe,
          }}
        />
      </Box>

      {isMood
        ? [0, 1, 2, 3].map((i) => {
            const left = 18 + (i % 2) * 38;
            const top = 16 + Math.floor(i / 2) * 42;
            const rot = [-6, 5, -3, 7][i];
            return (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: `${left}%`,
                  top,
                  width: 56,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: i === 1 ? alpha(accent, 0.18) : "#fff",
                  border: "1.5px solid",
                  borderColor: alpha(accent, 0.32 + i * 0.05),
                  boxShadow: `0 8px 18px ${alpha(accent, 0.14)}`,
                  display: "grid",
                  placeItems: "center",
                  "--from-rot": `${rot - 12}deg`,
                  "--to-rot": `${rot}deg`,
                  animation: `${fanIn} 520ms cubic-bezier(0.22, 1, 0.36, 1) both, ${bob} ${
                    2.1 + i * 0.2
                  }s ease-in-out infinite`,
                  animationDelay: `${0.12 + i * 0.08}s, ${0.5 + i * 0.1}s`,
                  ...motionSafe,
                }}
              >
                <DashboardCustomizeOutlinedIcon
                  sx={{ fontSize: 22, color: accent, opacity: 0.85 }}
                />
              </Box>
            );
          })
        : [-18, 0, 18].map((rot, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                left: "50%",
                top: 22,
                width: 58,
                height: 78,
                ml: `${-29 + i * 4}px`,
                borderRadius: 1.5,
                bgcolor: i === 1 ? alpha(accent, 0.16) : "#fff",
                border: "1.5px solid",
                borderColor: alpha(accent, 0.34 + i * 0.06),
                boxShadow: `0 10px 20px ${alpha(accent, 0.14)}`,
                display: "grid",
                placeItems: "center",
                zIndex: i + 1,
                "--from-rot": "0deg",
                "--to-rot": `${rot}deg`,
                transformOrigin: "50% 90%",
                animation: `${fanIn} 560ms cubic-bezier(0.22, 1, 0.36, 1) both, ${bob} ${
                  2.2 + i * 0.15
                }s ease-in-out infinite`,
                animationDelay: `${0.1 + i * 0.1}s, ${0.55 + i * 0.12}s`,
                ...motionSafe,
              }}
            >
              {i === 1 ? (
                <LayersOutlinedIcon sx={{ fontSize: 26, color: accent }} />
              ) : (
                <DescriptionOutlinedIcon
                  sx={{ fontSize: 22, color: accent, opacity: 0.8 }}
                />
              )}
            </Box>
          ))}

      <Box
        sx={{
          position: "absolute",
          right: 14,
          bottom: 12,
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.18),
          border: "1px solid",
          borderColor: alpha(accent, 0.42),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.2)}`,
          animation: `${cloudLift} 2.5s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {isMood ? (
          <AutoAwesomeIcon sx={{ fontSize: 18, color: accent }} />
        ) : (
          <Typography
            sx={{ fontSize: 13, fontWeight: 900, color: accent, lineHeight: 1 }}
          >
            3
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ChoiceCard({
  accent,
  title,
  subtitle,
  icon,
  primary,
  onClick,
  delay,
  disabled = false,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        px: 1.5,
        py: 1.25,
        borderRadius: 2.25,
        border: "1.5px solid",
        borderColor: disabled
          ? alpha(accent, 0.14)
          : primary
            ? accent
            : alpha(accent, 0.28),
        bgcolor: disabled
          ? alpha(accent, 0.04)
          : primary
            ? alpha(accent, 0.1)
            : alpha(accent, 0.03),
        opacity: disabled ? 0.55 : 1,
        boxShadow: disabled
          ? "none"
          : primary
            ? `0 10px 24px ${alpha(accent, 0.18)}`
            : `0 4px 12px ${alpha(accent, 0.06)}`,
        transition:
          "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background 160ms ease",
        animation: `${staggerIn} 380ms cubic-bezier(0.22, 1, 0.36, 1) both`,
        animationDelay: `${delay || 0}ms`,
        ...motionSafe,
        ...(disabled
          ? {}
          : {
              "&:hover": {
                transform: "translateY(-2px) scale(1.01)",
                borderColor: accent,
                bgcolor: alpha(accent, primary ? 0.14 : 0.08),
                boxShadow: `0 14px 28px ${alpha(accent, 0.2)}`,
              },
              "&:active": { transform: "translateY(0) scale(0.99)" },
            }),
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.75,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(accent, primary ? 0.22 : 0.12),
          color: accent,
          animation: `${bob} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "0.9rem",
            lineHeight: 1.25,
            color: "text.primary",
          }}
          noWrap
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 0.15 }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default function CreateFichaTecnicaDialog({
  open,
  titulo,
  onTituloChange,
  onClose,
  onCreate,
  creating = false,
  error = "",
  treeHint = "",
  /** "ficha" | "moodboard" */
  variant = "ficha",
  /** Boards ya creados desde este AI Design */
  existingBoards = [],
  onOpenExisting,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMood = variant === "moodboard";
  const accent = isMood ? ACCENT_MOOD : ACCENT_FICHA;
  const hasExisting = (existingBoards || []).length > 0;
  const [step, setStep] = useState(hasExisting ? "choose" : "create");

  useEffect(() => {
    if (!open) return;
    setStep((existingBoards || []).length > 0 ? "choose" : "create");
  }, [open, existingBoards]);

  const chooseLead = isMood
    ? t("presentacionesSearch.newDesign.moodboardReuseLead", {
        defaultValue:
          "Ya hay moodboard(s) de este AI Design. ¿Abrimos el mismo o creamos uno nuevo?",
      })
    : t("presentacionesSearch.newDesign.fichaReuseLead", {
        defaultValue:
          "Ya hay ficha(s) técnica(s) de este AI Design. ¿Abrimos la misma o creamos una nueva?",
      });

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={{ enter: 280, exit: 160 }}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          borderRadius: 3.5,
          borderColor: alpha(accent, 0.22),
          boxShadow: `0 28px 64px ${alpha(accent, 0.22)}, 0 8px 24px rgba(15,23,42,0.18)`,
          background: isDark
            ? `linear-gradient(165deg, ${alpha(accent, 0.12)} 0%, ${theme.palette.background.paper} 42%)`
            : `linear-gradient(165deg, ${alpha(accent, 0.09)} 0%, #fff 48%)`,
          animation: open
            ? `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`
            : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2.75,
          pt: 2.25,
          pb: 1.5,
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: alpha(accent, 0.14),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 12% 0%, ${alpha(
              accent,
              0.22,
            )} 0%, transparent 55%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            animation: `${staggerIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
            ...motionSafe,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.16),
              color: accent,
              border: `1px solid ${alpha(accent, 0.28)}`,
              animation: `${cloudLift} 2.8s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            {isMood ? (
              <DashboardCustomizeOutlinedIcon sx={{ fontSize: 24 }} />
            ) : (
              <DescriptionOutlinedIcon sx={{ fontSize: 24 }} />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.2 }}
            >
              {step === "choose"
                ? isMood
                  ? t("presentacionesSearch.newDesign.moodboardReuseTitle", {
                      defaultValue: "Moodboard existente",
                    })
                  : t("presentacionesSearch.newDesign.fichaReuseTitle", {
                      defaultValue: "Ficha técnica existente",
                    })
                : isMood
                  ? t("presentacionesSearch.newDesign.createMoodboard", {
                      defaultValue: "Crear moodboard",
                    })
                  : t("presentacionesSearch.newDesign.createFicha", {
                      defaultValue: "Crear ficha técnica",
                    })}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.55, lineHeight: 1.45 }}
            >
              {step === "choose"
                ? chooseLead
                : isMood
                  ? t("presentacionesSearch.newDesign.moodboardLead", {
                      defaultValue:
                        "Crea un moodboard en Compás con las versiones de este AI Design. Luego puedes volver aquí.",
                    })
                  : t("presentacionesSearch.newDesign.fichaLead", {
                      defaultValue:
                        "Crea un moodboard Ficha Técnica con 3 páginas: Dibujos, Siluetas y Trimmings.",
                    })}
            </Typography>
          </Box>
        </Box>
        <HeroStage accent={accent} variant={variant} />
      </Box>

      <DialogContent sx={{ pt: 2.25, px: 2.75 }}>
        {error ? (
          <Alert
            severity="error"
            sx={{
              mb: 1.5,
              animation: `${staggerIn} 280ms ease both`,
              ...motionSafe,
            }}
          >
            {error}
          </Alert>
        ) : null}
        {step === "create" && treeHint ? (
          <Alert
            severity="info"
            sx={{
              mb: 1.5,
              borderColor: alpha(accent, 0.25),
              animation: `${staggerIn} 320ms ease both`,
              ...motionSafe,
            }}
          >
            {treeHint}
          </Alert>
        ) : null}

        {step === "choose" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
            {(existingBoards || []).map((board, idx) => {
              const empty =
                board?.isEmpty === true ||
                (board?.assetCount != null && !(Number(board.assetCount) > 0));
              return (
                <ChoiceCard
                  key={board.id}
                  accent={accent}
                  delay={80 + idx * 60}
                  primary={idx === 0 && !empty}
                  disabled={Boolean(empty) || creating}
                  icon={<OpenInNewOutlinedIcon sx={{ fontSize: 20 }} />}
                  title={t("presentacionesSearch.newDesign.openExistingNamed", {
                    defaultValue: "Abrir «{{title}}»",
                    title: board.Titulo || `#${board.id}`,
                  })}
                  subtitle={
                    empty
                      ? t("presentacionesSearch.newDesign.openExistingEmpty", {
                          defaultValue:
                            "Sin elementos (se borraron). Crea uno nuevo o vuelve a volcar el historial.",
                        })
                      : t("presentacionesSearch.newDesign.openExistingHint", {
                          defaultValue:
                            "Vuelca el historial nuevo y abre en Compás",
                        })
                  }
                  onClick={() => !creating && !empty && onOpenExisting?.(board)}
                />
              );
            })}
            <ChoiceCard
              accent={accent}
              delay={80 + (existingBoards?.length || 0) * 60}
              primary={false}
              icon={<AddOutlinedIcon sx={{ fontSize: 22 }} />}
              title={
                isMood
                  ? t("presentacionesSearch.newDesign.createNewMoodboard", {
                      defaultValue: "Crear un moodboard nuevo",
                    })
                  : t("presentacionesSearch.newDesign.createNewFicha", {
                      defaultValue: "Crear una ficha nueva",
                    })
              }
              subtitle={t("presentacionesSearch.newDesign.createNewHint", {
                defaultValue: "Con el histórico actual de este AI Design",
              })}
              onClick={() => !creating && setStep("create")}
            />
          </Box>
        ) : (
          <Box
            sx={{
              animation: `${staggerIn} 340ms cubic-bezier(0.22, 1, 0.36, 1) both`,
              ...motionSafe,
            }}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t("presentacionesSearch.newDesign.fichaTitleLabel", {
                defaultValue: "Título",
              })}
              value={titulo}
              onChange={(e) => onTituloChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !creating) {
                  e.preventDefault();
                  onCreate?.();
                }
              }}
              disabled={creating}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.02),
                  transition: "box-shadow 160ms ease, border-color 160ms ease",
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 3px ${alpha(accent, 0.18)}`,
                  },
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: accent,
                  },
                "& .MuiInputLabel-root.Mui-focused": { color: accent },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.75,
          pb: 2.25,
          pt: 0.5,
          gap: 1,
          animation: `${staggerIn} 400ms ease both`,
          animationDelay: "80ms",
          ...motionSafe,
        }}
      >
        {step === "create" && hasExisting ? (
          <Button
            onClick={() => setStep("choose")}
            disabled={creating}
            sx={{ textTransform: "none", mr: "auto", fontWeight: 700 }}
          >
            {t("presentacionesSearch.newDesign.backToChoice", {
              defaultValue: "Atrás",
            })}
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          disabled={creating}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("presentacionesSearch.newDesign.sketchView.cancel", {
            defaultValue: "Cancelar",
          })}
        </Button>
        {step === "create" ? (
          <Button
            variant="contained"
            onClick={onCreate}
            disabled={creating || !String(titulo || "").trim()}
            startIcon={
              creating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              px: 2.25,
              borderRadius: 2,
              boxShadow: `0 8px 20px ${alpha(accent, 0.35)}`,
              transition: "transform 140ms ease, box-shadow 140ms ease",
              "&:hover": {
                bgcolor: alpha(accent, 0.9),
                boxShadow: `0 12px 26px ${alpha(accent, 0.42)}`,
                transform: "translateY(-1px)",
              },
              "&.Mui-disabled": {
                bgcolor: alpha(accent, 0.35),
                color: "#fff",
              },
              minWidth: 118,
            }}
          >
            {creating
              ? t("presentacionesSearch.newDesign.creating", {
                  defaultValue: "Creando…",
                })
              : t("presentacionesSearch.newDesign.fichaCreate", {
                  defaultValue: "Crear",
                })}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
